import Box from '@mui/material/Box'
import Paper from '@mui/material/Paper'
import Typography from '@mui/material/Typography'
import Table from '@mui/material/Table'
import TableBody from '@mui/material/TableBody'
import TableCell from '@mui/material/TableCell'
import TableHead from '@mui/material/TableHead'
import TableRow from '@mui/material/TableRow'
import Tooltip from '@mui/material/Tooltip'
import Alert from '@mui/material/Alert'
import { ADMIN_API_URL } from 'const'
import Refreshing from 'components/core/Refreshing'
import { SectionHeading, Tile } from '../Cost/parts'
import { fmtCount } from '../StreamIngest/vocabulary'
import FriendPushTable, {
  KIND_WORDS as FRIEND_KIND_WORDS,
} from './FriendPushTable'
import { QUOTE_PUSH_OUTCOMES, QUOTE_PUSH_SKIPS } from './vocabulary'

// The Pushes tab's first half: how the pushes were delivered, before what
// they led to (reclip-api `stream/metrics/loop/delivery.py`, from Mongo). The
// Loop page reads it once (`Flow/reads.js`) for this and its Loop panel.
//
// - **Quote pushes** (step 4b): each quote's push outcome as the selection
//   pass decided it, per UTC processing day. Only a failed send is a loss;
//   a skip is policy and is counted, never coloured.
// - **Friend pushes** (step 6b), moved here from the Pipeline page: the held
//   rows, "{name} has a quote you can hear" and comments, by state and
//   outcome. Held past due — a held row more than twice its grace late, or a
//   send whose claim was abandoned — is the one sign a Cloud Task or a send was
//   lost. Every number is a count of rows that exist.

const QuotePushTable = ({ days }) => {
  const known = new Set(QUOTE_PUSH_OUTCOMES.map(({ key }) => key))
  const extra = [
    ...new Set(
      days.flatMap(({ counts }) =>
        Object.keys(counts).filter((key) => !known.has(key))
      )
    ),
  ].map((key) => ({ key, label: key, help: 'Not named on this page yet.' }))
  const columns = [...QUOTE_PUSH_OUTCOMES, ...extra]
  return (
    <Box sx={{ overflowX: 'auto' }}>
      <Table size="small" sx={{ minWidth: 760 }}>
        <TableHead>
          <TableRow>
            <TableCell>Day (UTC)</TableCell>
            {columns.map(({ key, label, help }) => (
              <TableCell key={key} align="right">
                <Tooltip title={help}>
                  <span>{label}</span>
                </Tooltip>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {days.map(({ day, counts }) => (
            <TableRow key={day}>
              <TableCell>{day}</TableCell>
              {columns.map(({ key, tone }) => {
                const n = counts[key] || 0
                return (
                  <TableCell
                    key={key}
                    align="right"
                    sx={{
                      color: n ? tone : 'text.disabled',
                      fontWeight: n && tone ? 700 : undefined,
                    }}
                  >
                    {fmtCount(n)}
                  </TableCell>
                )
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </Box>
  )
}

const QuotePushes = ({ days }) => {
  const total = (key) =>
    days.reduce((sum, row) => sum + (row.counts[key] || 0), 0)
  const failed = total('push_failed')
  const skipped = QUOTE_PUSH_SKIPS.reduce((sum, { key }) => sum + total(key), 0)
  return (
    <Box sx={{ mb: 4 }}>
      <SectionHeading
        title="A new quote of theirs"
        help="Each quote’s push, on the day the quote was picked."
      />
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        <Tile
          label="Pushed"
          value={fmtCount(total('pushed'))}
          help="Quotes whose push Braze took in the window."
        />
        <Tile
          label="Failed"
          value={fmtCount(failed)}
          tone={failed > 0 ? 'error.main' : undefined}
          help="Braze refused the push or never answered, even after retries. Red when there’s one."
        />
        <Tile
          label="Not pushed, by design"
          value={fmtCount(skipped)}
          help="Skipped on purpose, by the reasons in the table: never a failure."
        />
      </Box>
      {days.length ? (
        <QuotePushTable days={days} />
      ) : (
        <Typography variant="body2" color="text.secondary">
          No quote was pushed or skipped in this window.
        </Typography>
      )}
    </Box>
  )
}

// The admin API on this machine reads its own settings, which a local env
// file leaves without the queue on purpose: say that, rather than that the
// environment has none.
const LOCAL_API = /localhost|127\.0\.0\.1/.test(ADMIN_API_URL || '')

// `sends` is the delivery read's `friend_pushes`: each push sent or dropped,
// one per send. Held and sending are the rows as they stand now. An API from
// before sends were counted carries only the rows, which count a push sent
// twice once.
const FriendPushes = ({ friends, sends }) => {
  const now = (friends.days || []).filter(
    ({ state }) => state === 'held' || state === 'sending'
  )
  const settled =
    sends ??
    (friends.days || []).filter(
      ({ state }) => state === 'sent' || state === 'dropped'
    )
  const rows = [...settled, ...now]
  const totals = rows.reduce((sum, row) => {
    sum[row.state] = (sum[row.state] || 0) + row.count
    if (row.outcome === 'send_failed' || row.outcome === 'lost')
      sum.lost = (sum.lost || 0) + row.count
    return sum
  }, {})
  const minutes = (seconds) =>
    seconds == null ? '—' : `${Math.round(seconds / 60)} min`
  return (
    <Box>
      <SectionHeading
        title="Friend pushes"
        help="Friends' new quotes, comments and friend requests. New quotes and comments are held a short grace first, then sent or dropped; held past due means a task or a send was lost."
      />
      {!friends.queue_configured && (
        <Alert severity="info" sx={{ mb: 2 }}>
          {LOCAL_API
            ? 'This local admin server has no STREAM_SOCIAL_QUEUE, as local env files leave it unset so nothing here queues a deployed task. It says nothing about the environment’s own queue; the rows below are the environment’s.'
            : 'Queue unset (STREAM_SOCIAL_QUEUE): nothing is armed, so no friend push is held or sent here. Tasks already queued still fire.'}
        </Alert>
      )}
      <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 2, mb: 2 }}>
        {Object.entries(friends.held_past_due || {}).map(([kind, n]) => (
          <Tile
            key={kind}
            label={`Held past due · ${FRIEND_KIND_WORDS[kind] || kind}`}
            value={fmtCount(n)}
            tone={n > 0 ? 'warning.main' : undefined}
            help={`Held more than ${minutes(
              2 * (friends.grace_s?.[kind] ?? 0)
            )}, twice their wait, or stuck mid-send: a task or a send was lost. Counts every push now, whatever the window.`}
          />
        ))}
        <Tile
          label="Held · sending"
          value={`${fmtCount(totals.held || 0)} · ${fmtCount(
            totals.sending || 0
          )}`}
          help="Pushes held now, waiting to send, or mid-send."
        />
        <Tile
          label="Sent · dropped"
          value={`${fmtCount(totals.sent || 0)} · ${fmtCount(
            totals.dropped || 0
          )}`}
          tone={totals.lost > 0 ? 'error.main' : undefined}
          help={`Sent in the window, each send counted, and dropped at send time. Red: a send that failed or was lost (${fmtCount(
            totals.lost || 0
          )}).`}
        />
      </Box>
      <FriendPushTable days={rows} />
    </Box>
  )
}

// `answer` is the delivery read (`useLoopReads`), `null` until it lands.
const Delivery = ({ answer, error, loading }) => {
  return (
    <Refreshing active={loading}>
      <Paper
        id="delivery"
        variant="outlined"
        sx={{
          p: { mobile: 2, desktop: 3 },
          borderRadius: 2,
          mb: 3,
          scrollMarginTop: 24,
        }}
      >
        <Typography variant="subtitle1" fontWeight={700} sx={{ mb: 2 }}>
          How the pushes were delivered
        </Typography>
        {error ? (
          <Alert severity="error">{error}</Alert>
        ) : answer ? (
          <>
            <QuotePushes days={answer.quotes || []} />
            <FriendPushes
              friends={answer.friends || {}}
              sends={answer.friend_pushes}
            />
          </>
        ) : null}
      </Paper>
    </Refreshing>
  )
}

export default Delivery
